import React, { FC, useEffect } from 'react';
import { User } from 't2-graphql-types';
import ArticleInspire from './ArticleInspire';
import { useReactiveAuthenticatedUser } from '../Auth/hooks/useAuthenticatedUser';
import { getUsersInspiredByArticle } from './queries/getUsersInspiredByArticle';
import Spinner from '../Spinner/Spinner';
import { useToggleArticleInspireMutation } from './hooks/useToggleArticleInspireMutation';
import { useIsInspiredByArticle } from './hooks/useIsInspiredByArticle';
import { useModal } from '../Modal/useModal';
import { AnalyticsModalType } from '../Modal/constants';
import { useLikesAnalyticsEvents } from './hooks/useLikesAnalyticsEvents';
import { ArticleType } from '../Article/types';
import { ArticleInspirePosition } from './constants';
import { showNotification } from '../NotificationBanner/showNotification';
import { WalletConnectionError } from '../../services/walletConnection/constants';
import { useT2Query } from '../../hooks/useT2Query';

type TArticleInspireContainerProps = {
  articleId: string;
  articleType: ArticleType;
  position: ArticleInspirePosition;
};

type TUsersInspiredByArticleResponse = {
  id: string;
  userArticleRelations: {
    user: Pick<User, 'id' | 'name' | 'publicAddress' | 'profileImage'>;
    timestamp: string;
  }[];
};

const ArticleInspireContainer: FC<TArticleInspireContainerProps> = ({ articleId, articleType, position }) => {
  const analytics = useLikesAnalyticsEvents();
  const { authenticatedUserId: userId } = useReactiveAuthenticatedUser() || {};
  const { openModal, ModalComponent } = useModal({ analytics: { modalName: AnalyticsModalType.likes } });

  const [
    fetchIsInspiredByArticle,
    { data: isInspiredByArticleData, loading: isInspiredByArticleLoading, error, refetch: refetchIsInspiredByArticle },
  ] = useIsInspiredByArticle({ articleId, userId });

  const {
    data: usersInspiredByArticleData,
    refetch: refetchUsersInspiredByArticle,
    loading: isUsersInspiredByArticleLoading,
  } = useT2Query<TUsersInspiredByArticleResponse>(getUsersInspiredByArticle, {
    variables: {
      articleId,
    },
  });
  const usersInspiredByArticle =
    usersInspiredByArticleData?.userArticleRelations?.map(({ user, timestamp }) => ({
      ...user,
      timestamp,
    })) || [];
  const [mutateInspiration, { loading: isToggleArticleInspireInProgress }] = useToggleArticleInspireMutation({
    articleId,
  });
  const inspireRelationsCount = isInspiredByArticleData?.userArticleRelations?.length;
  const isUserInspired = !!inspireRelationsCount && inspireRelationsCount > 0;
  const isActionInProgress =
    isToggleArticleInspireInProgress || isInspiredByArticleLoading || isUsersInspiredByArticleLoading;
  const totalInspirationsCount = usersInspiredByArticle.length || 0;

  const handleClickOnInspire = async () => {
    try {
      await mutateInspiration();
      await refetchIsInspiredByArticle();
      await refetchUsersInspiredByArticle();

      analytics.sendLikeButtonClickEvent({
        relatedItemId: articleId,
        relatedItemType: articleType,
        position,
        destination: 't2',
        value: !isUserInspired,
      });
    } catch (error) {
      const message = (error as any)?.message;
      if (message === WalletConnectionError.WrongAccount) {
        showNotification({
          title: 'Error',
          type: 'error',
          message: 'Wallet mismatch. Please connect with the same wallet you used to create your account.',
        });
        return;
      }

      showNotification({ title: 'Error', type: 'error', message: 'Something went wrong. Please try again.' });
    }
  };

  const handleArticleInspirationsCountClick = () => {
    analytics.sendLikeCounterClickEvent({ relatedItemId: articleId, relatedItemType: articleType, position });
    openModal();
  };

  useEffect(() => {
    if (!isInspiredByArticleLoading && !error && userId) {
      fetchIsInspiredByArticle();
    }
  }, [isInspiredByArticleLoading, error, userId]);

  if (isUsersInspiredByArticleLoading) {
    return <Spinner color="gray" size={10} />;
  }

  if (error) {
    return null;
  }

  return (
    <ArticleInspire
      onArticleInspirationsCounterClick={handleArticleInspirationsCountClick}
      ModalComponent={ModalComponent}
      isActionInProgress={isActionInProgress}
      isUserInspired={isUserInspired}
      onInspireClick={handleClickOnInspire}
      usersInspiredByArticle={usersInspiredByArticle}
      totalInspirationsCount={totalInspirationsCount}
    />
  );
};

export default ArticleInspireContainer;
